import clsx from 'clsx';
import { motion } from 'motion/react';
import Image from 'next/image';
import { ScrollArea, ScrollBar } from '@music-vine/cadence/ui';
import { ChannelIDInput } from './ChannelIDInput';
import { PLACEHOLDER_IMG_URL } from '@/constants/images';
import { SearchIcon } from '@music-vine/cadence/icons';
import { Input } from '@music-vine/cadence/ui';
import type { YouTubeChannelRequest, YoutubeResponseItem } from './types';
interface ChannelResultProps {
  compact?: boolean;
  disabled?: boolean;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChannel: (selectedValue: YouTubeChannelRequest) => void;
  handleSubmitChannelID: (id: string) => void;
  inputValue: string;
  isPaused: boolean;
  searchResults: YoutubeResponseItem[];
}
export const ChannelResults: React.FC<ChannelResultProps> = ({
  inputValue,
  handleChange,
  searchResults,
  handleSelectChannel,
  handleSubmitChannelID,
  compact,
  disabled,
  isPaused
}) => {
  const displayedResults = compact ? searchResults.slice(0, 4) : searchResults;
  const resultsHeight = displayedResults.length * 52 + (displayedResults.length ? 12 : 0);
  return <div className={clsx('absolute top-0 flex min-h-full w-full flex-col justify-center rounded-xl bg-gray-100 p-4 font-sans transition-all dark:bg-gray-800', !!searchResults.length && !compact && 'shadow-card', !!compact && 'relative! overflow-auto p-3!', !!disabled && 'pointer-events-none', isPaused && 'hidden')} data-sentry-component="ChannelResults" data-sentry-source-file="ChannelResults.tsx">
      <div className="relative flex w-full flex-col">
        <Input autoComplete="off" autoFocus className="bg-[var(--whitelisting-input-override-bg)]!" disabled={disabled} name="youtube_channel" onChange={handleChange} placeholder={'Enter your channel name'} rightIcon={<SearchIcon className="size-3" />} type="text" value={inputValue} data-sentry-element="Input" data-sentry-source-file="ChannelResults.tsx" />
      </div>
      <ScrollArea className={clsx('custom-scrollbar w-full', Array.isArray(searchResults) && searchResults.length > 0 && 'h-[200px]')} fadeAway scrollHideDelay={0} style={{
      '--fade-away-from': '#edeef0',
      '--fade-away-to': '#edeef000',
      '--fade-away-from-dark': '#3d4653',
      '--fade-away-to-dark': '#3d465300'
    } as React.CSSProperties} data-sentry-element="ScrollArea" data-sentry-source-file="ChannelResults.tsx">
        <motion.div animate={{
        height: resultsHeight,
        paddingTop: displayedResults.length ? 12 : 0
      }} className={clsx('flex flex-col gap-3 overflow-hidden py-4', !!compact && 'border-0!')} exit={{
        height: 0,
        padding: '0 0'
      }} initial={{
        height: 0,
        padding: '0'
      }} transition={{
        type: 'spring',
        duration: 0.6,
        bounce: 0
      }} data-sentry-element="motion.div" data-sentry-source-file="ChannelResults.tsx">
          {displayedResults.map(value => <button className="flex cursor-pointer items-center rounded-[1.25rem] transition-[margin] duration-200 hover:ms-1 hover:bg-[var(--search-dropdown-item-bg)] focus:ms-4 focus:bg-[var(--search-dropdown-item-bg)]" disabled={disabled} key={value.snippet?.channelId} onClick={() => {
          if (!disabled) {
            handleSelectChannel(value.snippet);
          }
        }} type="button">
              <Image alt={`Channel thumbnail for ${value.snippet?.title}`} className="h-10 w-10 min-w-10 rounded-full bg-gray-300 dark:bg-gray-700" height="40" onError={e => {
            e.currentTarget.src = PLACEHOLDER_IMG_URL;
          }} src={value.snippet?.thumbnails?.default?.url || PLACEHOLDER_IMG_URL} width="40" />
              <p className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap ps-4 font-sans font-semibold text-[var(--h2-c)] text-xs">
                {value.snippet?.title}
              </p>
            </button>)}
        </motion.div>
        <ScrollBar className="w-3 rounded-full bg-gray-100 dark:bg-gray-950" orientation="vertical" data-sentry-element="ScrollBar" data-sentry-source-file="ChannelResults.tsx">
          <div className="mx-auto w-2 rounded-full dark:bg-white" />
        </ScrollBar>
      </ScrollArea>
      {!compact && Array.isArray(searchResults) && searchResults.length > 0 && <ChannelIDInput disabled={disabled} handleSubmitChannelID={handleSubmitChannelID} />}
    </div>;
};